/* eslint-disable  @typescript-eslint/no-explicit-any */
import {Component, Event, type EventEmitter, h, Listen, Prop, Watch} from '@stencil/core';
import type {FilterNode} from '../../../result-page/facets-build-wrapper/filter-node';
import {type FiltersSelectedAnalyticsData} from './filters-selected-mobile.model';
import {FILTERS_ID_SEPARATOR} from '../../../../constants/filterConstants';
import type {CoseRequestService} from '../../../../utils/services/cose/cose-request.service';
import {QueryContext} from '../../../../utils/services/cose/cose-request.service';
import {filter, Subject, takeUntil} from 'rxjs';
import {MobileFilteringCategoryType} from '../models/mobile-filtering-category-type.enum';
import {MobileFilteringActionType} from '../models/mobile-filtering-action-type.enum';
import type {ObservableMap} from '@stencil/store';
import {findParentValueInNestedArrayObject} from '../../../../utils/helpers/find-parent-value-in-nested-array-object';
import type {FacetResponse, InSearchEventModel} from '../../../../components';
import {makeLogger} from '../../../../logger/logger';

const {info, err} = makeLogger({pathToCurrentFile: 'components/design-system/mobile-filters/mobile-filters-analytics/mobile-filters-analytics.tsx'});

@Component({
  tag: 'uiaas-search-mobile-filters-analytics',
  shadow: false,
})
export class MobileFiltersAnalytics {
  private unsubscriber$: Subject<any> = new Subject<any>();
  private facets: Array<FacetResponse>;
  @Prop() readonly store: ObservableMap<FiltersSelectedAnalyticsData>;

  @Prop({ attribute: 'coseRequestService' }) readonly coseRequestService?: CoseRequestService;
  @Prop({ attribute: 'tab-name'}) readonly tabName: string;

  @Event({
    eventName: 'search:mobileFilteringBehaviors',
    bubbles: true
  })
    searchMobileFilteringBehaviors: EventEmitter<any>;

  @Event({
    eventName: 'search:mobileFilteringFacetClickEvent',
    bubbles: true
  })
    searchMobileFilteringFacetClickEvent: EventEmitter<any>;

  @Event({
    eventName: 'search:mobileFilteringChipClick',
    bubbles: true
  })
    searchMobileFilteringChipClick: EventEmitter<any>;

  @Event({
    eventName: 'facest:onInsearchInput',
    composed: true,
    cancelable: true,
    bubbles: true
  }) onInSearchInputEmitter: EventEmitter<InSearchEventModel>;

  @Listen('search:mobileFilteringOpen')
  onMobileFilteringAccordingShow(event: CustomEvent) {
      const {tabName, siteSearchKeyword} = event.detail;
      this.searchMobileFilteringBehaviors.emit({
        mainCategory: 'filter_check',
        behaviorAction: 'click',
        actionType: MobileFilteringActionType.FilterOpen,
        secondCategory: null,
        tabName: tabName,
        siteSearchKeyword: siteSearchKeyword
      });
  }

  @Listen('search:mobileFilteringClose')
  onMobileFilteringClose(event: CustomEvent) {
      const {tabName, siteSearchKeyword} = event.detail;
      this.searchMobileFilteringBehaviors.emit({
        mainCategory: 'filter_check',
        behaviorAction: 'click',
        actionType: MobileFilteringActionType.FilterClose,
        secondCategory: null,
        tabName: tabName,
        siteSearchKeyword: siteSearchKeyword
      });
  }

  @Listen('on:chip:remove:mobile')
  onChipRemove(event: CustomEvent) {
      const {id, filterCount, clickedArea} = event.detail;
      const parsedUniqueId = id.split(FILTERS_ID_SEPARATOR);
      const selectedFilters: FilterNode[] = [...this.store.get('selectedFilters')];
      const indexSelected = selectedFilters.findIndex(filter => filter.uniqueId === event.detail.id);
      const facetDisplayName = findParentValueInNestedArrayObject(this.facets, 'displayName', 'uniqueId', id, 'children');

      if (indexSelected !== -1) {
        selectedFilters.splice(indexSelected, 1);
        this.store.set( 'selectedFilters', selectedFilters);
      }

      this.searchMobileFilteringChipClick.emit({
        event: 'filterClick',
        clickedArea,
        filterAction: MobileFilteringActionType.FilterDeselected,
        filterCategory: facetDisplayName,
        filterName: decodeURIComponent(parsedUniqueId[parsedUniqueId.length - 1]),
        filterCount,
        tabName: this.coseRequestService.context.tabName
      });
  }

  @Listen('filter:did:change:mobile')
  filterDidChange(event: CustomEvent) {
      const queryContext: QueryContext = this.coseRequestService.onQueryContext$.value;
      const filterNode = event.detail;
      const selectedFilters: FilterNode[] = [...this.store.get('selectedFilters')];
      const deselectedFilters: FilterNode[] = [...this.store.get('deselectedFilters')];
      const indexSelected = selectedFilters.findIndex(filter => filter.uniqueId === filterNode.uniqueId);
      const indexDeselected = deselectedFilters.findIndex(filter => filter.uniqueId === filterNode.uniqueId);

      if(filterNode.selected) {
        if (indexSelected === -1) {
          selectedFilters.push(filterNode);
        }
        if (indexDeselected !== -1) {
          deselectedFilters.splice(indexDeselected, 1);
        }
      }

      if(!filterNode.selected) {
        if (indexDeselected === -1 && queryContext === QueryContext.mobileFilteringShowResults) {
          deselectedFilters.push(filterNode);
        }

        if (indexSelected !== -1) {
          selectedFilters.splice(indexSelected, 1);
        }
      }

      this.store.set( 'selectedFilters', selectedFilters);
      this.store.set( 'deselectedFilters', deselectedFilters);
  }


  @Listen('search:mobileFilteringButtonResultsClick')
  onMobileFilteringButtonResultsClick(event: CustomEvent) {
      const {context, tabName, siteSearchKeyword} = event.detail;

      if(context === QueryContext.mobileFilteringShowResults) {
        this.emitFiltersChangeEvents();
      }

      this.store.set( 'deselectedFilters', []);

      this.searchMobileFilteringBehaviors.emit({
        mainCategory: 'filter_check',
        behaviorAction: 'click',
        actionType: MobileFilteringActionType.FilterSelected,
        secondCategory: 'show_results',
        tabName,
        siteSearchKeyword
      });
  }

  @Listen('search:mobileFilteringResetButtonClick')
  onMobileFilteringResetButtonClick(event: CustomEvent) {
      const {context, tabName, siteSearchKeyword} = event.detail;

      if(context === QueryContext.mobileFilteringShowResults) {
        this.emitFiltersChangeEvents(true);
      }

      this.store.reset();

      this.searchMobileFilteringBehaviors.emit({
        mainCategory: 'filter_check',
        behaviorAction: 'click',
        actionType: MobileFilteringActionType.ResetFilters,
        secondCategory: MobileFilteringCategoryType.ResetFilters,
        tabName,
        siteSearchKeyword
      });
  }

  @Listen('insearch:characteristicsInput')
  onInSearchCharacteristicsInput(event: CustomEvent) {
      this.onInSearchInputEmitter.emit(
        {
          category: 'search-filters',
          action: 'click',
          clickedArea: 'inCharacteristicsSearch',
          eventName: event.detail.event,
          siteSearchKeyword: event.detail.siteSearchKeyword,
          tabName: this.tabName,
          facetCategory: event.detail.facetCategory
        });
  }

  private emitFiltersChangeEvents(resetSelected = false): void {
      this.store.state.selectedEventsList.forEach(
        eventData => {
            const facetDisplayName = findParentValueInNestedArrayObject(this.facets, 'displayName', 'uniqueId', eventData?.uniqueId, 'children');
            if(resetSelected) {
              eventData.actionType = MobileFilteringActionType.FilterDeselected;
              eventData.filterAction = MobileFilteringActionType.FilterDeselected;
            }
            this.searchMobileFilteringFacetClickEvent.emit({...eventData, filterCategory: facetDisplayName});
        }
      );
      this.store.state.deselectedEventsList.forEach(
        eventData => {
            const facetDisplayName = findParentValueInNestedArrayObject(this.facets, 'displayName', 'uniqueId', eventData?.uniqueId, 'children');
            eventData.actionType = MobileFilteringActionType.FilterDeselected;
            eventData.filterAction = MobileFilteringActionType.FilterDeselected;
            this.searchMobileFilteringFacetClickEvent.emit({...eventData, filterCategory: facetDisplayName});
        }
      );
  }

  componentWillLoad() {
      try{
        this.coseRequestService.facetsV2Service.facetsMobileService.facetsOptions
          .pipe(
            takeUntil(this.unsubscriber$),
            filter((fc) => fc !== null)
          )
          .subscribe((fc) => {
              this.facets = fc?.facets;
          });
      } catch(error) {
        err('Error fetching facet display name:', error);
      }

      this.store.onChange('selectedFilters', (value: FilterNode[]) => {
          this.store.state.selectedEventsList = [...value].map(filterNode => {
              const parsedUniqueId = filterNode.uniqueId.split(FILTERS_ID_SEPARATOR);
              return {
                event: 'filterClick',
                filterAction: MobileFilteringActionType.FilterSelected,
                filterCategory: decodeURIComponent(parsedUniqueId[0]),
                filterName: decodeURIComponent(parsedUniqueId[parsedUniqueId.length - 1]),
                site_search_category: MobileFilteringCategoryType.ShowResults,
                actionType: MobileFilteringActionType.FilterSelected,
                tabName: this.tabName,
                filterCount: filterNode.count,
                uniqueId: filterNode.uniqueId,
                filterSearchInputValue: filterNode.filterSearchInput || ''
              };
          });
      });
      this.store.onChange('deselectedFilters', (value: FilterNode[]) => {
          this.store.state.deselectedEventsList = [...value].map(filterNode => {
              const parsedUniqueId = filterNode.uniqueId.split(FILTERS_ID_SEPARATOR);
              return {
                event: 'filterClick',
                filterAction: MobileFilteringActionType.FilterDeselected,
                filterCategory: decodeURIComponent(parsedUniqueId[0]),
                filterName: decodeURIComponent(parsedUniqueId[parsedUniqueId.length - 1]),
                site_search_category: MobileFilteringCategoryType.ShowResults,
                actionType: MobileFilteringActionType.FilterDeselected,
                tabName: this.tabName,
                filterCount: filterNode.count,
                uniqueId: filterNode.uniqueId,
                filterSearchInputValue: filterNode.filterSearchInput || ''
              };
          });
      });
  }

  disconnectedCallback() {
      this.store.reset();
      this.unsubscriber$.next(null);
      this.unsubscriber$.complete();
  }

  render() {
      return <slot/>;
  }
}
